import type { ComponentProps } from 'react'
import type { OrganizationId } from '@/lib/organization-scoping'
import type {
  SuperTicketPreview,
  SuperTicketTemplate,
  SuperTicketTemplateSettings,
} from '@/lib/services/super-ticket-settings/super-ticket-settings.types'
import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowRight, ExternalLink } from 'lucide-react'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { superTicketQueries } from '@/lib/services/super-ticket-settings/super-ticket-settings.query'
import { initialSuperTicketLayout } from '@/lib/services/super-ticket-settings/super-ticket-settings.schema'
import { salesOrderPrintTemplateOptionsByValue } from '@/lib/services/sales-orders/sales-order-print-templates'
import {
  getSuperTicketPrintPreferences,
  rememberSuperTicketTemplate,
  getStoredPreprintedBrandingByTemplate,
} from '@/lib/super-ticket-print-preferences'
import { SuperTicketEditor } from './SuperTicketEditor'

/** A customized super ticket the dialog hands back, ready for a printer. */
export type SuperTicketPrintRequest = SuperTicketPreview & {
  template: SuperTicketTemplate
  clientJobId: string
  /** Called once the printer job is finished, so the next print starts a new one. */
  onIntentReleased: () => void
}
type PrintConfigurationProps = {
  organizationId: OrganizationId
  orderId: number | string
  onPrint: (request: SuperTicketPrintRequest) => unknown
  onOrderSummary?: () => unknown
  onClose: () => void
}
type SuperTicketPrintDialogProps = Omit<PrintConfigurationProps, 'onClose'> & {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SuperTicketPrintDialog({
  open,
  onOpenChange,
  organizationId,
  orderId,
  onPrint,
  onOrderSummary,
}: SuperTicketPrintDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open ? (
        <DialogContent
          size="wide"
          viewport="scroll"
          padding="compact"
          spacing="compact"
          aria-describedby={undefined}
        >
          <DialogHeader className="flex-row flex-wrap items-baseline gap-x-4 pr-8">
            <DialogTitle>Print Super Ticket</DialogTitle>
          </DialogHeader>
          <PrintConfiguration
            key={`${organizationId}:${orderId}`}
            organizationId={organizationId}
            orderId={orderId}
            onPrint={onPrint}
            onOrderSummary={onOrderSummary}
            onClose={() => onOpenChange(false)}
          />
        </DialogContent>
      ) : null}
    </Dialog>
  )
}

function PrintConfiguration({
  organizationId,
  orderId,
  onPrint,
  onOrderSummary,
  onClose,
}: PrintConfigurationProps) {
  const path = usePathHelper()
  const [template, setTemplate] = useState<string>(() => getSuperTicketPrintPreferences().template)
  const catalog = useQuery({
    ...superTicketQueries.catalog({ organizationId }),
    refetchOnMount: 'always',
  })
  const [templates, setTemplates] = useState<SuperTicketTemplateSettings[] | null>(null)
  const intent = useRef<{ key: string; id: string } | null>(null)
  // Each opening captures fresh defaults once; later refreshes must not replace this print's edits.
  if (!templates && catalog.isSuccess && catalog.isFetchedAfterMount) setTemplates(catalog.data)
  if (!templates && !catalog.isError)
    return (
      <Typography variant="description" role="status">
        Loading ticket settings…
      </Typography>
    )
  if (!templates)
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Ticket settings could not be loaded.{' '}
          <Button type="button" variant="link" onClick={() => catalog.refetch()}>
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    )
  const metadata = templates.find((entry) => entry.template === template) ?? templates[0]
  if (!metadata) return null
  const purchaseUrl = salesOrderPrintTemplateOptionsByValue[metadata.template]?.purchaseUrl
  const layout = initialSuperTicketLayout(
    metadata,
    getStoredPreprintedBrandingByTemplate()[metadata.template]
  )
  const submit = ({ html, layout }: SuperTicketPreview) => {
    const key = JSON.stringify([metadata.template, layout])
    if (intent.current?.key !== key) intent.current = { key, id: crypto.randomUUID() }
    const submittedIntent = intent.current
    return onPrint({
      html,
      layout,
      template: metadata.template,
      clientJobId: submittedIntent.id,
      onIntentReleased: () => {
        // An old recovery toast must not reset a newer customized print.
        if (intent.current === submittedIntent) intent.current = null
      },
    })
  }
  return (
    <SuperTicketEditor
      key={metadata.template}
      organizationId={organizationId}
      metadata={metadata}
      initialLayout={layout}
      resetLayout={layout}
      salesOrderId={orderId}
      oneOff
      toolbar={({ isSubmitting }) => (
        <div className="flex min-w-0 basis-full flex-wrap items-center gap-x-4 gap-y-2 @3xl/ticket-editor:basis-auto">
          <div className="flex min-w-0 max-w-full items-center gap-2">
            <Label className="shrink-0" htmlFor="super-ticket-template">
              Paper template
            </Label>
            <Select
              value={metadata.template}
              disabled={isSubmitting}
              onValueChange={(value) => {
                setTemplate(value)
                rememberSuperTicketTemplate(value)
                intent.current = null
              }}
            >
              <SelectTrigger id="super-ticket-template" className="min-w-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {templates.map((entry) => (
                  <SelectItem value={entry.template} key={entry.template}>
                    {entry.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {purchaseUrl ? (
            <Typography
              as="a"
              variant="caption"
              tone="interaction"
              interaction="link"
              className="inline-flex items-center gap-1.5"
              {...({
                href: purchaseUrl,
                target: '_blank',
                rel: 'noreferrer',
              } satisfies ComponentProps<'a'>)}
            >
              Buy paper
              <ExternalLink className="size-3.5 shrink-0" aria-hidden="true" />
            </Typography>
          ) : (
            <Typography variant="description">Plain Letter paper</Typography>
          )}
        </div>
      )}
      submitLabel="Print Super Ticket"
      footer={
        <Typography
          as={Link}
          variant="description"
          tone="interaction"
          interaction="link"
          className="inline-flex items-center gap-1.5"
          {...path.supertickets.index().using({ orgId: organizationId }).link}
          onClick={onClose}
        >
          Change superticket printing defaults
          <ArrowRight className="size-4 shrink-0" aria-hidden="true" />
        </Typography>
      }
      onSubmit={submit}
      onOrderSummary={onOrderSummary}
      onCancel={onClose}
    />
  )
}
